import {Flex, Title, Separator} from '@priority-software/priority-style-react';
import React, {memo, useRef} from 'react';
import {FieldActions} from './useFieldSelect';
import styles from './styles.module.scss';
import SearchInput from '../SearchInput';
import BreadCrumbs from './BreadCrumbs';
import FieldSelectList from './FieldSelectList';
import FieldSelectedList from './FieldSelectedList';
import {DataInclusion} from '../DataInclusion/DataInclusion';
import texts from '../../texts';
import {FieldsSelectDlgID} from '../../consts';
import {SkeltonLoader} from '../../../../components/Common/SkelatonLoader/SkelatonLoader';
import {createDataTestId} from '../../../../utils/dataTestId';
import {CircularLoader} from '../../../../components/CircularLoader/CircularLoader';

interface Props extends FieldActions {
    searchQuery: string;
    changeQuery: (query: string) => void;
    isLoadingFields: boolean;
    outerJoinId: number;
}
const Content = memo(
    ({
        zoom,
        fields,
        isLoading,
        selectedNodes,
        handleZoom,
        handleSelectField,
        handleBackZoom,
        searchQuery,
        isLoadingFields,
        changeQuery,
        outerJoinId
    }: Props) => {
        const onZoom = (nodeId: string, title: string) => {
            !!searchQuery && changeQuery('');
            handleZoom(nodeId, title);
        };

        const onBackZoom = (index: number) => {
            !!searchQuery && changeQuery('');
            handleBackZoom(index);
        };
        
        // Create a ref for the field list scrollbar
        const fieldListScrollbarRef = useRef(null);

        return !isLoadingFields || selectedNodes ? (
            <Flex
                direction="column"
                alignItems="flex-start"
                componentClasses={styles.contentContainer}
            >
                <Title
                    skin="h4"
                    textColor="primary-700"
                    text={texts.selectFieldTitle}
                    componentClasses={styles.dialogTitle}
                />

                <Separator align="vertical" componentClasses={styles.titleSeparator} />
                <div className={styles.selectionContainer} id={FieldsSelectDlgID}>
                    <Flex
                        alignItems="flex-start"
                        componentClasses={styles.contentSection}
                        direction="column"
                        {...createDataTestId('new-report-select-fields-section', true)}
                    >
                        <Title
                            skin="h5"
                            textColor="primary-700"
                            {...createDataTestId('new-report-select-fields-title', true)}
                        >
                            {texts.availableFields}
                        </Title>
                        <div className={styles.searchInputWrapper}>
                            <SearchInput
                                inputClasses={styles.availableFieldsSearchInput}
                                searchQuery={searchQuery}
                                onChangeQuery={changeQuery}
                                roundedCorners
                                scrollbarRef={fieldListScrollbarRef}
                            />
                        </div>
                        <BreadCrumbs items={zoom.map(z => z.title)} onClick={onBackZoom} />
                        {isLoading || isLoadingFields ? (
                            <SkeltonLoader width={300} height={450} />
                        ) : (
                            <FieldSelectList
                                currentForm={zoom[zoom.length - 1]}
                                searchQuery={searchQuery}
                                fields={fields}
                                isSelected={id => selectedNodes?.has(id)}
                                onSelect={handleSelectField}
                                onZoom={onZoom}
                                scrollbarRef={fieldListScrollbarRef}
                            />
                        )}
                    </Flex>
                    <Flex
                        alignItems="flex-start"
                        componentClasses={styles.contentSection}
                        direction="column"
                        {...createDataTestId('new-report-selected-fields-section', true)}
                    >
                        <Flex>
                            <Title
                                skin="h5"
                                componentClasses={styles.selectedFieldsTitle}
                                textColor="primary-700"
                                text={texts.selected.replace('X', selectedNodes?.size || '0')}
                                {...createDataTestId('new-report-selected-fields-title', true)}
                            />
                            {false && (
                                <DataInclusion
                                    selectedCols={
                                        selectedNodes ? Array.from(selectedNodes.values()) : []
                                    }
                                    outerJoinId={outerJoinId}
                                />
                            )}
                        </Flex>
                        <FieldSelectedList
                            selected={selectedNodes ? Array.from(selectedNodes.values()) : []}
                            onRemove={handleSelectField}
                            currentForm={zoom[zoom.length - 1]}
                        />
                    </Flex>
                </div>
            </Flex>
        ) : (
            <CircularLoader />
        );
    }
);

export default Content;
